import React, { useState } from 'react';
import axios from 'axios';

const ServiceTypesList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [serviceData, setServiceData] = useState([
    {
      id: 1,
      name: 'SDK Service',
      category: 'Core',
      status: 'Active',
      description: 'eg. Document Processing SDK, Image Recognition SDK, etc.'
    },
    {
      id: 2,
      name: 'API Service',
      category: 'Core',
      status: 'Active',
      description: 'eg. Chatbot API, Image Recognition API, Video Analysis API, etc.'
    },
    {
      id: 2,
      name: 'Platform Service',
      category: 'Core',
      status: 'Active',
      description: 'eg. Visitor Management Platform'
    }
  ]);
  

  const emptyService = {
    id: 'new',
    name: '',
    category: 'Core',
    status: 'Active',
    description: ''
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditingId('new');
    setEditedData(emptyService);
    setError(null);
    setServiceData([emptyService, ...serviceData]);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (editingId === 'new') {
        response = await axios.post('/api/services', editedData);
        if (response.status === 201) {
          const updatedData = serviceData.filter(service => service.id !== 'new');
          setServiceData([response.data, ...updatedData]);
          setIsAddingNew(false);
        }
      } else {
        response = await axios.put(`/api/services/${editedData.id}`, editedData);
        if (response.status === 200) {
          const updatedData = serviceData.map(item => 
            item.id === editedData.id ? response.data : item
          );
          setServiceData(updatedData);
        }
      }
      setEditingId(null);
      setEditedData({});
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving changes');
      console.error('Error saving service:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isAddingNew) {
      setServiceData(serviceData.filter(service => service.id !== 'new'));
      setIsAddingNew(false);
    }
    setEditingId(null);
    setEditedData({});
    setError(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditedData(item);
    setError(null);
  };

  const handleDelete = async (item) => {
    if (window.confirm('Are you sure you want to delete this service type?')) {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.delete(`/api/services/${item.id}`);
        if (response.status === 200) {
          setServiceData(serviceData.filter(service => service.id !== item.id));
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting service');
        console.error('Error deleting service:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (field, value) => {
    setEditedData({
      ...editedData,
      [field]: value
    });
    setError(null);
  };

  const filteredData = serviceData.filter(item =>
    item.id === 'new' || 
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mt-4">
      {error && (
        <div className="alert alert-danger mb-4" role="alert">
          {error}
        </div>
      )}
      
      <div className="listing-header d-flex justify-content-between align-items-center">
        <h2 className="listing-title">Service Types</h2>
        <button 
          className="btn-add-new"
          onClick={handleAddNew}
          disabled={loading || isAddingNew}
        >
          <i className="fas fa-plus-circle"></i>
          <span>Add New Service</span>
        </button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          className="form-control listing-search"
          placeholder="Search Services..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="listing-table">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th className="text-start">Name</th>
                <th className="text-start">Category</th>
                <th className="text-start">Status</th>
                <th className="text-start">Description</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item) => (
                <tr key={item.id} className={item.id === 'new' ? 'new-item-row' : ''}>
                  <td>
                    {editingId === item.id ? (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editedData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Enter service name"
                        autoFocus
                      />
                    ) : (
                      item.name
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <select
                        className="form-select form-select-sm"
                        value={editedData.category}
                        onChange={(e) => handleChange('category', e.target.value)}
                      >
                        <option value="Core">Core</option>
                        <option value="Add-on">Add-on</option>
                        <option value="Integration">Integration</option>
                      </select>
                    ) : (
                      item.category
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <select
                        className="form-select form-select-sm"
                        value={editedData.status}
                        onChange={(e) => handleChange('status', e.target.value)}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Beta">Beta</option>
                      </select>
                    ) : (
                      <span className={`badge bg-${item.status === 'Active' ? 'success' : 
                        item.status === 'Inactive' ? 'danger' : 'warning'}`}>
                        {item.status}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editedData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        placeholder="Enter description"
                      />
                    ) : (
                      item.description
                    )}
                  </td>
                  <td>
                    <div className="action-buttons text-end">
                      {editingId === item.id ? (
                        <>
                          <button 
                            className="btn btn-outline-success btn-icon"
                            onClick={handleSave}
                            disabled={loading}
                          >
                            {loading ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-check"></i>
                            )}
                          </button>
                          <button 
                            className="btn btn-outline-secondary btn-icon ms-2"
                            onClick={handleCancel}
                            disabled={loading}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            className="btn btn-outline-primary btn-icon"
                            onClick={() => handleEdit(item)}
                            disabled={loading}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button 
                            className="btn btn-outline-danger btn-icon ms-2"
                            onClick={() => handleDelete(item)}
                            disabled={loading}
                          >
                            {loading ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-trash"></i>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ServiceTypesList;